import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import {
  ALL_RISK_LEVEL_VALUE,
  CARD_ORDERING_LABELS,
  CARD_ORDERING_VALUES,
  RISK_LEVELS_LABELS,
  SET_CARDS_ORDERING,
  SET_RISK_LEVEL_SELECTED_FILTERS,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectCardsOrdering,
  selectLevelCountsData,
  selectRiskLevelSelectedFilters,
} from 'containers/SpearPhishingAgent/selectors'

import Dropdown from 'components/lib/DesignSystem/Dropdown'
import { ChevronDown } from 'components/lib/DesignSystem/Icons'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'

const Container = styled.div`
  margin-bottom: 20px;
`

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 1000px) {
    gap: 10px;
  }
`

const ChipsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1 1 auto;

  @media (max-width: 1000px) {
    gap: 10px;
  }
`

const OrderingPrefix = styled.span`
  color: var(--ds-gray-3);
`

const Counter = styled.div`
  min-width: 20px;
  height: 20px;
  border-radius: ${({ $rounded }) =>
    $rounded ? 'var(--t-border-radius-rounded)' : 'var(--t-border-radius-md)'};
  background-color: ${({ $active }) =>
    $active ? 'var(--psima-counter-active-bg)' : 'var(--psima-counter-inactive-bg)'};
  color: ${({ $active }) =>
    $active ? 'var(--psima-counter-active-text)' : 'var(--psima-counter-inactive-text)'};
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--t-spacing-0-5) var(--t-spacing-1-5);
`

// Override V3 Button's TERTIARY+floating (hardcoded white bg / blue-50 border) and
// SPECIAL (text uses var(--white) which resolves to #1a1d24 in dark) so the risk
// filter chips and sort trigger render correctly in both themes. Uses `background:`
// (not `background-color:`) so dark-mode tokens can supply a glassmorphic gradient.
//
// The :hover / :focus / :focus-visible / :focus-within / :active selectors are
// all explicit (and target the outer <button>) so they out-specificity V3's
// own :hover/:active/:focus-visible rules on the inner span — V3 applies
// var(--t-colors-blue-100) (≈ near-white) on those states, which would otherwise
// flash a white background over our dark-mode glassmorphic chip after click.
const filterChipInteractiveShadow = (shadowToken) => css`
  & > button:not(:disabled):hover > span,
  & > button:not(:disabled):focus > span,
  & > button:not(:disabled):focus-visible > span,
  & > button:not(:disabled):focus-within > span,
  & > button:not(:disabled):active > span {
    box-shadow: ${shadowToken};
  }

  & > button:not(:disabled):focus-visible > span {
    box-shadow:
      ${shadowToken},
      0 0 0 3px var(--input-focus-ring-default);
  }
`

const defaultFilterChipStyles = css`
  & > button > span {
    background: var(--psima-filter-chip-bg);
    border-color: var(--psima-filter-chip-border);
    color: var(--psima-filter-chip-text);
    box-shadow: var(--psima-filter-chip-shadow);
  }
  & > button:not(:disabled):hover > span,
  & > button:not(:disabled):focus > span,
  & > button:not(:disabled):focus-visible > span,
  & > button:not(:disabled):focus-within > span,
  & > button:not(:disabled):active > span {
    background: var(--psima-filter-chip-bg-hover);
    border-color: var(--psima-filter-chip-border-hover);
    color: var(--psima-filter-chip-text);
  }
  ${filterChipInteractiveShadow('var(--psima-filter-chip-shadow)')}
`

const selectedFilterChipStyles = css`
  & > button > span {
    color: var(--psima-filter-chip-selected-text);
    background: var(--psima-filter-chip-selected-bg);
    border-color: var(--psima-filter-chip-selected-border);
    box-shadow: var(--psima-filter-chip-selected-shadow);
  }
  & > button:not(:disabled):hover > span,
  & > button:not(:disabled):focus > span,
  & > button:not(:disabled):focus-visible > span,
  & > button:not(:disabled):focus-within > span,
  & > button:not(:disabled):active > span {
    background: var(--psima-filter-chip-selected-bg-hover);
    border-color: var(--psima-filter-chip-selected-border);
    color: var(--psima-filter-chip-selected-text);
  }
  ${filterChipInteractiveShadow('var(--psima-filter-chip-selected-shadow)')}
`

const FilterButtonWrapper = styled.span`
  ${({ $selected }) => ($selected ? selectedFilterChipStyles : defaultFilterChipStyles)}
`

const OrderingTrigger = styled.span`
  display: inline-flex;
  ${defaultFilterChipStyles}
`

const FiltersSection = () => {
  const dispatch = useDispatch()
  const riskLevelSelectedFilters = useSelector(selectRiskLevelSelectedFilters)
  const levelCountsData = useSelector(selectLevelCountsData)
  const ordering = useSelector(selectCardsOrdering)
  const cards = {
    total: levelCountsData.data?.total ?? 0,
    byLevel: levelCountsData.data?.byLevel ?? {},
  }

  const orderingOptions = Object.values(CARD_ORDERING_VALUES).map((value) => ({
    value,
    label: CARD_ORDERING_LABELS[value],
  }))

  const onOrderingChange = (value) => {
    if (value === ordering) return

    dispatch({ type: SET_CARDS_ORDERING, payload: { value } })
  }

  const onRiskLevelClick = (riskLevel) => {
    const isOnlyALlSelected =
      riskLevelSelectedFilters.length === 1 && riskLevelSelectedFilters[0] === ALL_RISK_LEVEL_VALUE

    if (isOnlyALlSelected && riskLevel === ALL_RISK_LEVEL_VALUE) return

    let newRiskLevelSelectedFilters = [...riskLevelSelectedFilters]

    if (riskLevel === ALL_RISK_LEVEL_VALUE) {
      newRiskLevelSelectedFilters = [ALL_RISK_LEVEL_VALUE]
    } else if (isOnlyALlSelected) {
      newRiskLevelSelectedFilters = [riskLevel]
    } else {
      const isRiskLevelSelected = riskLevelSelectedFilters.includes(riskLevel)
      if (isRiskLevelSelected) {
        newRiskLevelSelectedFilters = newRiskLevelSelectedFilters.filter(
          (selectedRiskLevel) => selectedRiskLevel !== riskLevel
        )
      } else {
        newRiskLevelSelectedFilters.push(riskLevel)
      }
    }

    const isSingleSpecificLevelSelected =
      newRiskLevelSelectedFilters.length === 1 &&
      newRiskLevelSelectedFilters[0] !== ALL_RISK_LEVEL_VALUE

    if (isSingleSpecificLevelSelected && ordering === CARD_ORDERING_VALUES.LEVEL) {
      dispatch({
        type: SET_CARDS_ORDERING,
        payload: { value: CARD_ORDERING_VALUES.MOST_RECENT },
      })
    }

    dispatch({
      type: SET_RISK_LEVEL_SELECTED_FILTERS,
      payload: { value: newRiskLevelSelectedFilters },
    })
  }

  useEffect(() => {
    if (!riskLevelSelectedFilters.length) {
      dispatch({
        type: SET_RISK_LEVEL_SELECTED_FILTERS,
        payload: { value: [ALL_RISK_LEVEL_VALUE] },
      })
    }
  }, [riskLevelSelectedFilters])

  const isAllSelected = riskLevelSelectedFilters.includes(ALL_RISK_LEVEL_VALUE)

  return (
    <Container>
      <FiltersContainer>
        <ChipsGroup>
          <FilterButtonWrapper $selected={isAllSelected}>
            <Button
              id="Psima_Dashboard_RiskLevel_Button-All"
              dataTestId="Psima_Dashboard_RiskLevel_Button-All"
              ariaPressed={isAllSelected}
              floating
              variant={isAllSelected ? VARIANTS.SPECIAL : VARIANTS.TERTIARY}
              rightElement={
                <Counter
                  data-testid="Psima_Dashboard_RiskLevel_Counter-All"
                  $rounded={cards.total.toString().length < 9}
                  $active={isAllSelected}
                >
                  {cards.total}
                </Counter>
              }
              onClick={() => onRiskLevelClick(ALL_RISK_LEVEL_VALUE)}
              size={SIZES.MEDIUM}
              disabled={cards.total === 0}
            >
              All Risk Levels
            </Button>
          </FilterButtonWrapper>
          {Object.entries(RISK_LEVELS_LABELS)
            .reverse()
            .map(([riskLevel, riskLevelLabel]) => {
              const isSelected = riskLevelSelectedFilters.includes(riskLevel)
              return (
                <FilterButtonWrapper key={riskLevel} $selected={isSelected}>
                  <Button
                    id={`Psima_Dashboard_RiskLevel_Button-${riskLevel}`}
                    floating
                    dataTestId={`Psima_Dashboard_RiskLevel_Button-${riskLevel}`}
                    ariaPressed={isSelected}
                    variant={isSelected ? VARIANTS.SPECIAL : VARIANTS.TERTIARY}
                    onClick={() => onRiskLevelClick(riskLevel)}
                    size={SIZES.MEDIUM}
                    rightElement={
                      <Counter
                        data-testid={`Psima_Dashboard_RiskLevel_Counter-${riskLevel}`}
                        $rounded={cards.byLevel[riskLevel]?.toString().length < 9}
                        $active={isSelected}
                      >
                        {cards.byLevel[riskLevel]}
                      </Counter>
                    }
                    disabled={cards.byLevel[riskLevel] === 0}
                  >
                    {`${riskLevelLabel} Risk`}
                  </Button>
                </FilterButtonWrapper>
              )
            })}
        </ChipsGroup>
        <Dropdown
          options={orderingOptions}
          selectedValue={ordering}
          onSelect={onOrderingChange}
          placement="bottomRight"
          dropdownStyle={{ minWidth: '200px' }}
        >
          <OrderingTrigger>
            <Button
              id="Psima_Dashboard_Ordering_Button"
              dataTestId="Psima_Dashboard_Ordering_Button"
              floating
              variant={VARIANTS.TERTIARY}
              size={SIZES.MEDIUM}
              rightElement={<ChevronDown width="16px" height="16px" />}
            >
              <OrderingPrefix>Sort by: </OrderingPrefix>
              {CARD_ORDERING_LABELS[ordering]}
            </Button>
          </OrderingTrigger>
        </Dropdown>
      </FiltersContainer>
    </Container>
  )
}

export default FiltersSection
